import type { MutableRefObject } from 'react';
import { useCallback } from 'react';

import { useAuth0 } from '@auth0/auth0-react';
import type { PayloadAction, Store } from '@reduxjs/toolkit';
import {
  resetDraftArtifactVersions,
  setDraftArtifactVersions,
} from 'ducks/chat/chatSlice';
import { resetDraftArtifactVersionsV2 } from 'ducks/chat/chatV2Slice';
import { setIsNewDraftChanges } from 'ducks/layout/layoutSlice';
import Long from 'long';

import { ClientEnvironment } from '@polyai/common/constants/global.constants';
import { getActionForEvent } from '@polyai/stream/commands/mapper/DONOTEDIT';
import { MAIN_BRANCH_ID } from '@polyai/stream/constants';
import type {
  EventBatch,
  Notification,
} from '@polyai/stream/protobuf/commands';
import type { RootState as ProjectionState } from '@polyai/stream/store';

import type { RootState } from 'reduxStore';
import { resetChat } from 'actions';
import type { GetProjectionResponse } from 'api/resources/projection';
import { useLocalDispatch } from 'hooks/reduxHooks';
import { useIsMultiDraft } from 'hooks/useIsMultiDraft';
import { useToast } from 'hooks/useToast';
import { hydrateProjection } from 'store/reducers/hydration';
import {
  clearOptimisticCommands,
  removeOptimisticCommandIds,
  setBranchId,
  setLastKnownSequence,
} from 'store/reducers/stream-tracker';
import { agentStreamLogger } from '../constants';
import type { ActionMeta } from '../types';

interface EventProcessorParams {
  accountId: string;
  projectId: string;
  branchId: string;
  store: Store<RootState>;
  acceptedState: MutableRefObject<ProjectionState>;
  refetchProjection?: () => Promise<any>;
}

interface EventProcessorState {
  highestSequenceId: number;
  actions: PayloadAction<any, any, ActionMeta>[];
  commandIds: string[];
  draftMerged: boolean;
  draftDeleted: boolean;
  eventsFromAnotherUser: boolean;
  deployment?: {
    artifactVersion: string;
    lambdaDeploymentVersion?: string;
  };
}

export const useEventProcessor = ({
  accountId,
  projectId,
  branchId,
  store,
  acceptedState,
  refetchProjection,
}: EventProcessorParams) => {
  const dispatch = useLocalDispatch();
  const toast = useToast();
  const { user } = useAuth0();

  const isMultiDraft = useIsMultiDraft();

  const handleServerEvents = useCallback(
    (batch: EventBatch) => {
      // FLOW:
      // 1. Set reducer state back to acceptedState
      // 2. Apply all new events, regardless of if they have been optimistically applied
      // 3. Set acceptedState to be this new state
      // 4. Dedupe the optimisticCommands to remove those who we have just received events for
      // 5. Apply the remaining unvalidated optimistic commands
      // 6. Update lastKnownSequence to the highest sequence id that is actually validated

      // Rehydrating the reducer state to the acceptedState is not actually as memory intensive
      // as expected, so that's why we decided to use this flow as it is cleaner.
      // In the possible case that this becomes a bottleneck, we will consider migrating to a more
      // memory efficient algorithm.

      // step 1 — only rewind if there are optimistic commands to unwind.
      // When the queue is empty, store.getState() === acceptedState.current
      // by construction (step 3 maintains it), so the reset is a no-op that
      // still triggers applyHydration and re-renders every connected component.
      if (store.getState().streamTracker.optimisticCommands.length > 0) {
        const currentLastKnownSequence =
          store.getState().streamTracker.lastKnownSequence;
        dispatch(
          hydrateProjection({
            sequenceId: Number(currentLastKnownSequence),
            projection: acceptedState.current,
          }),
        );
      }

      // step 2
      const {
        highestSequenceId,
        actions,
        commandIds,
        draftMerged,
        draftDeleted,
        eventsFromAnotherUser,
        deployment,
      } = batch.events.reduce(
        (
          {
            highestSequenceId,
            actions,
            commandIds,
            draftMerged,
            draftDeleted,
            eventsFromAnotherUser,
            deployment,
          },
          event,
        ) => {
          const sequenceId = event.sequence.toInt();
          const commandId = event.commandId;

          if (event.payload?.$case === 'branchMerged') {
            return {
              highestSequenceId: Math.max(highestSequenceId, sequenceId),
              actions,
              commandIds: [...commandIds, commandId],
              draftMerged: true,
              draftDeleted: false,
              eventsFromAnotherUser: false,
              deployment: deployment,
            };
          }

          if (event.payload?.$case === 'branchDeleted') {
            return {
              highestSequenceId: Math.max(highestSequenceId, sequenceId),
              actions,
              commandIds: [...commandIds, commandId],
              draftMerged: false,
              draftDeleted: true,
              eventsFromAnotherUser: false,
              deployment: deployment,
            };
          }

          if (event.payload?.$case === 'deploymentCompleted') {
            return {
              highestSequenceId: Math.max(highestSequenceId, sequenceId),
              actions,
              commandIds: [...commandIds, commandId],
              draftMerged,
              draftDeleted,
              eventsFromAnotherUser,
              deployment: {
                artifactVersion: event.payload.value.artifactVersion,
                lambdaDeploymentVersion:
                  event.payload.value.lambdaDeploymentVersion,
              },
            };
          }

          if (event.payload) {
            const action = getActionForEvent(event.payload.$case);
            actions.push({
              ...action(event.payload.value),
              meta: {
                createdAt:
                  event.metadata?.createdAt?.toISOString() ||
                  new Date().toISOString(),
                createdBy: event.metadata?.createdBy || 'Unknown user',
              },
            });
          }

          return {
            highestSequenceId: Math.max(highestSequenceId, sequenceId),
            actions,
            commandIds: [...commandIds, commandId],
            draftMerged,
            draftDeleted,
            eventsFromAnotherUser:
              eventsFromAnotherUser ||
              event.metadata?.createdBy !== user?.email,
            deployment,
          };
        },
        {
          highestSequenceId: 0,
          actions: [],
          commandIds: [],
          draftMerged: false,
          draftDeleted: false,
          eventsFromAnotherUser: false,
        } as EventProcessorState,
      );

      agentStreamLogger?.info('Received Events', {
        accountId,
        projectId,
        branchId,
        batch,
        highestSequenceId,
      });

      if (eventsFromAnotherUser) {
        dispatch(setIsNewDraftChanges(true));
      }

      if (deployment) {
        dispatch(
          setDraftArtifactVersions({
            artifactVersion: deployment.artifactVersion,
            lambdaDeployment: deployment.lambdaDeploymentVersion,
          }),
        );
      }

      if (draftMerged) {
        dispatch(setBranchId({ branchId: MAIN_BRANCH_ID }));
        return;
      }

      if (draftDeleted) {
        dispatch(setBranchId({ branchId: MAIN_BRANCH_ID }));
        return;
      }

      actions.forEach(action => {
        dispatch(action);
      });

      // step 3
      acceptedState.current = store.getState();

      // step 4
      dispatch(removeOptimisticCommandIds(commandIds));

      // step 5
      const remainingOptimisticCommands =
        store.getState().streamTracker.optimisticCommands;

      remainingOptimisticCommands.forEach(command => {
        dispatch(command.action);
      });

      // step 6
      dispatch(setLastKnownSequence({ sequenceId: highestSequenceId }));

      // Invalidate cached draft artifact versions on local changes
      // so the next chat start re-prepares
      if (!eventsFromAnotherUser && branchId !== MAIN_BRANCH_ID) {
        dispatch(resetDraftArtifactVersions());
        dispatch(resetDraftArtifactVersionsV2());
      }
    },
    [
      store,
      dispatch,
      acceptedState,
      accountId,
      projectId,
      branchId,
      user?.email,
    ],
  );

  const handleServerNotification = useCallback(
    (notification: Notification) => {
      if (
        notification.payload?.$case === 'branchMerged' &&
        notification.payload.value.branchId === branchId
      ) {
        agentStreamLogger?.info('Received Branch Merged Notification', {
          accountId,
          projectId,
          branchId,
        });
        if (notification.payload.value.mergedBy !== user?.email) {
          toast.info({
            title: `Branch has been merged by ${notification.payload.value.mergedBy}`,
          });
        }
        dispatch(setBranchId({ branchId: MAIN_BRANCH_ID }));
      }
      if (
        notification.payload?.$case === 'branchMerged' &&
        MAIN_BRANCH_ID === branchId
      ) {
        // When a branch is merged, if we're looking at the main branch we should refetch to receive the new projection
        void refetchProjection?.();
      }
      if (notification.payload?.$case === 'branchMerged') {
        dispatch(resetChat({ clientEnv: ClientEnvironment.SANDBOX }));
      }
      if (
        notification.payload?.$case === 'branchDeleted' &&
        notification.payload.value.branchId === branchId
      ) {
        agentStreamLogger?.info('Received Branch Deleted Notification', {
          accountId,
          projectId,
          branchId,
        });
        if (notification.payload.value.deletedBy !== user?.email) {
          toast.info({
            title: `Branch has been deleted by ${notification.payload.value.deletedBy}`,
          });
        }
        dispatch(setBranchId({ branchId: MAIN_BRANCH_ID }));
      }
      if (
        !isMultiDraft &&
        notification.payload?.$case === 'branchCreated' &&
        notification.payload.value.id !== branchId
      ) {
        dispatch(setBranchId({ branchId: notification.payload.value.id }));
      }
    },
    [
      branchId,
      isMultiDraft,
      accountId,
      projectId,
      user?.email,
      dispatch,
      toast,
      refetchProjection,
    ],
  );

  const handleServerProjection = useCallback(
    (projection: GetProjectionResponse) => {
      dispatch(
        hydrateProjection({
          sequenceId: Number(projection.lastKnownSequence),
          projection: projection.projection as unknown as RootState,
        }),
      );
      acceptedState.current = store.getState();

      const optimisticCommands =
        store.getState().streamTracker.optimisticCommands;
      if (!optimisticCommands.length) {
        return;
      }

      // If the optimistic sequence number is the same as the last known sequence + 1, we can re-apply the optimistic commands
      if (
        optimisticCommands[0].optimisticSequenceNumber ===
        Long.fromValue(projection.lastKnownSequence).toInt() + 1
      ) {
        for (const command of optimisticCommands) {
          dispatch(command.action);
        }
      } else {
        dispatch(clearOptimisticCommands());
      }
    },
    [dispatch, acceptedState, store],
  );

  return {
    handleServerEvents,
    handleServerNotification,
    handleServerProjection,
  };
};
